const CACHE_MS = 2 * 60 * 1000;
// A fresh native fix can take ~20 s: the app watches up to 10 s for an accurate fix, may ask the user to
// switch to precise location, and falls back to a single fix (mobile CRITICAL_FIXES #65).
const FRESH_NATIVE_TIMEOUT_MS = 30000;
// Once the app says it has the request (2.2+), the wait no longer counts against the 30 s: the first tap shows the
// system's location prompt, which waits for the reader, and the app always answers (a fix or null). This cap is
// only a backstop. Older apps never acknowledge and keep the 30 s.
const ACKED_NATIVE_TIMEOUT_MS = 3 * 60 * 1000;

// acc: accuracy radius in metres (null when unknown); precise: false when the user granted only an
// approximate location. Both come from app builds 2.2+; older builds send neither, so they read as
// unknown / precise and pages behave as before.
function fromNative(loc) {
  const acc = Number.isFinite(loc.acc) && loc.acc >= 0 ? loc.acc : null;
  return { lat: loc.lat, lon: loc.lon, acc, precise: loc.precise !== false };
}
let pending = [];      // one-shot getLocation() resolvers
let subscribers = [];  // long-lived page listeners

// Permanent owner of the native callback slot. Every native injection —
// REQUEST_LOCATION responses, app-start/resume syncs, two-phase refinements,
// permission-revoked nulls — lands here and fans out. Pages must never assign
// window.__onNativeLocation directly; use subscribeLocation() instead.
if (typeof window !== 'undefined') {
  window.__onNativeLocation = (loc) => {
    const result = loc && loc.lat != null && loc.lon != null ? fromNative(loc) : null;
    const waiters = pending; pending = [];
    waiters.forEach(({ resolve, timer }) => { clearTimeout(timer); resolve(result); });
    if (result) subscribers.forEach((cb) => { try { cb(result); } catch {} });
  };
  // The app has a fresh request in hand (it may be showing the location prompt): restart the fresh waiters' clocks
  // with the backstop. Before this, a reader who took over 30 s to answer the first prompt got «No se pudo obtener
  // tu ubicación» and the fix that followed was dropped (iOS simulator, 10 Oct 2026).
  window.__onNativeLocationAck = () => {
    pending.forEach((entry) => {
      if (!entry.fresh || entry.acked) return;
      entry.acked = true;
      clearTimeout(entry.timer);
      entry.timer = setTimeout(entry.expire, ACKED_NATIVE_TIMEOUT_MS);
    });
  };
}

/** Live location updates (refinements, permission grants). Returns unsubscribe. */
export function subscribeLocation(cb) {
  subscribers.push(cb);
  return () => { subscribers = subscribers.filter((s) => s !== cb); };
}

/**
 * Get the user's location. { fresh: true } forces a live GPS fix (locate buttons,
 * incident pins). Default allows a ≤2-min-old cached fix (passive page loads).
 * Resolves null on denial/timeout — callers show their own error UI.
 */
export function getLocation({ fresh = false, timeoutMs } = {}) {
  return new Promise((resolve) => {
    if (window.__isNativeApp) {
      const c = window.__nativeLocation;
      // ts is only injected by new binaries; old binaries' un-stamped cache is not trusted
      if (!fresh && c?.lat && c.ts && Date.now() - c.ts < CACHE_MS) return resolve(fromNative(c));
      if (timeoutMs == null) timeoutMs = fresh ? FRESH_NATIVE_TIMEOUT_MS : 15000;
      if (window.ReactNativeWebView) {
        const entry = { resolve, timer: null, fresh, acked: false };
        // Per-entry timeout: expires only this request, not other pending ones
        entry.expire = () => {
          pending = pending.filter((p) => p !== entry);
          resolve(null);
        };
        entry.timer = setTimeout(entry.expire, timeoutMs);
        pending.push(entry);
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'REQUEST_LOCATION', fresh }));
        return;
      }
      return resolve(null);
    }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          acc: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
          precise: true,
        }),
        () => resolve(null),
        fresh
          ? { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
          : { enableHighAccuracy: false, timeout: 8000, maximumAge: CACHE_MS }
      );
      return;
    }
    resolve(null);
  });
}

/**
 * A "use my location" tap: fresh fix, and (on the web, where the native bridge doesn't fan out)
 * also delivers it to subscribeLocation() listeners, so pages that subscribed on load update.
 * In the app this is the in-context moment for the permission prompt.
 */
export function locateMe() {
  return getLocation({ fresh: true }).then((loc) => {
    if (loc && !window.__isNativeApp) subscribers.forEach((cb) => { try { cb(loc); } catch {} });
    return loc;
  });
}

/**
 * Whether a position-based look-up (nearest stop, bins) should be asked again for `next`, given
 * the position it was last asked for: always the first time, when switching between an
 * approximate position (a barrio's centre) and a real fix, and after a move of at least `km`.
 */
export function movedEnough(prev, next, km) {
  if (!next) return false;
  if (!prev || !!prev.approximate !== !!next.approximate) return true;
  const rad = Math.PI / 180;
  const dLat = (next.lat - prev.lat) * rad;
  const dLon = (next.lon - prev.lon) * rad;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(prev.lat * rad) * Math.cos(next.lat * rad) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) >= km;
}

/** In the app: open the system settings for SomosTorre (to switch on precise location). */
export function openAppSettings() {
  window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'OPEN_SETTINGS' }));
}
